import { getAuthenticatedUserQueryKey, getUser } from "@/contexts"
import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import type { User } from "@/models/user"
import { fetchAuthorizedOrganizations } from "@/services/organization/useAuthorizedOrganizations"
import { createQueryKey } from "@/services/utils/createQueryKey"
import { queryClient } from "@/utils/queryClient"
import type { UsersState } from "@/utils/use-user-state"

const userStateStorageKey = `${process.env.REACT_APP_ENV}UserSessionInfo`

const INVALID_URL_ORG_IDS = new Set(["", "undefined", "null"])

const isAuthorizedUrlOrgId = (
  urlOrgId: string | undefined,
  authorizedOrgIds: string[]
): string | undefined => {
  if (!urlOrgId || INVALID_URL_ORG_IDS.has(urlOrgId)) {
    return undefined
  }

  return authorizedOrgIds.includes(urlOrgId) ? urlOrgId : undefined
}

interface ResolveOrganizationIdInput {
  urlOrgId: string | undefined
  savedOrgId: string | undefined
  defaultOrgId: string | undefined
  authorizedOrgIds: string[]
}

const resolveOrganizationId = ({
  urlOrgId,
  savedOrgId,
  defaultOrgId,
  authorizedOrgIds,
}: ResolveOrganizationIdInput): string | null => {
  if (authorizedOrgIds.length === 0) {
    return null
  }

  const authorizedUrlOrgId = isAuthorizedUrlOrgId(urlOrgId, authorizedOrgIds)
  if (authorizedUrlOrgId) {
    return authorizedUrlOrgId
  }

  if (savedOrgId && authorizedOrgIds.includes(savedOrgId)) {
    return savedOrgId
  }

  if (defaultOrgId && authorizedOrgIds.includes(defaultOrgId)) {
    return defaultOrgId
  }

  return authorizedOrgIds[0] ?? null
}

const getSavedOrganizationId = (userId: User["id"]): string | undefined => {
  if (!userId) {
    return undefined
  }

  try {
    const raw = localStorage.getItem(userStateStorageKey)
    if (!raw) {
      return undefined
    }

    const usersState = JSON.parse(raw) as UsersState
    const organizationId = usersState[userId]?.organizationId

    return organizationId && organizationId !== "" ? organizationId : undefined
  } catch {
    return undefined
  }
}

const getAuthorizedOrganizationsQueryKey = (organizationIds?: number[]) =>
  createQueryKey(ApiQueryName.AuthorizedOrganizations, "getMany", null, {
    organizationIds,
  })

interface OrganizationResolutionContext {
  user: User
  authorizedOrganizations: Organization[]
  resolvedOrganizationId: string | null
  resolvedOrganization: Organization | null
}

export const loadOrganizationResolutionContext = async (
  urlOrgId: string | undefined
): Promise<OrganizationResolutionContext | null> => {
  const user = await queryClient.fetchQuery({
    queryFn: getUser,
    queryKey: getAuthenticatedUserQueryKey,
  })

  if (!user) {
    return null
  }

  const authorizedOrganizations = await queryClient.fetchQuery({
    queryKey: getAuthorizedOrganizationsQueryKey(user.organizationIds),
    queryFn: () => fetchAuthorizedOrganizations(user.organizationIds),
  })

  const authorizedOrgIds = authorizedOrganizations.flatMap((organization) =>
    organization.id ? [organization.id] : []
  )
  const defaultOrgId = user.organizationIds[0]?.toString()
  const savedOrgId = getSavedOrganizationId(user.id)
  const resolvedOrganizationId = resolveOrganizationId({
    urlOrgId,
    savedOrgId,
    defaultOrgId,
    authorizedOrgIds,
  })

  const resolvedOrganization = resolvedOrganizationId
    ? (authorizedOrganizations.find(
        (organization) => organization.id === resolvedOrganizationId
      ) ?? null)
    : null

  return {
    user,
    authorizedOrganizations,
    resolvedOrganizationId,
    resolvedOrganization,
  }
}

export const buildPathWithResolvedOrgId = (
  pathname: string,
  currentOrgId: string,
  resolvedOrgId: string
): string => {
  const segments = pathname.split("/")

  if (segments[1] === currentOrgId) {
    segments[1] = resolvedOrgId
  }

  return segments.join("/") || "/"
}
